import api from '@/lib/axios';
import type { CreateClientContractDto } from '@/features/client-contracts/services/client-contracts.service';
import type { CreateContractDto as EmployeeCreateContractDto } from '@/features/employees/services/employees.service';

/** Extrai o nome do arquivo do header Content-Disposition (filename="" ou filename*=UTF-8''...). */
function parseFilenameFromContentDisposition(header: string | undefined): string | null {
  if (!header) return null;
  const matchStar = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (matchStar?.[1]) return decodeURIComponent(matchStar[1].trim());
  const match = header.match(/filename="([^"]*)"/);
  if (match?.[1]) {
    try {
      return decodeURIComponent(match[1].trim());
    } catch {
      return match[1].trim();
    }
  }
  return null;
}

export interface D4SignSafe {
  uuid_safe: string;
  'name-safe': string;
}

export type PlaceholderConfigItem = {
  label?: string;
  type?: string;
  /** Texto de exemplo exibido dentro do input quando vazio */
  example?: string;
  /** Fórmula que calcula o valor a partir de outros placeholders (ex: PRAZO_MESES * 30). O campo fica somente leitura. */
  formula?: string;
  suffixSingular?: string;
  suffixPlural?: string;
  /** Se true e type for email, o valor do placeholder é incluído na lista de assinantes do documento (ordem preservada). */
  isSigner?: boolean;
};

export interface WordTemplate {
  id: string;
  tenantId: string;
  name: string;
  description?: string | null;
  content?: string | null;
  googleDocsUrl?: string | null;
  docxStorageKey?: string | null;
  placeholders: string[];
  placeholderConfig?: Record<string, PlaceholderConfigItem> | null;
  targetType?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface D4signTemplate {
  id: string;
  tenantId: string;
  wordTemplateId?: string | null;
  d4signTemplateId: string;
  d4signSafeId: string;
  name: string;
  variables: string[];
  targetType?: string | null;
  driveFolderId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface D4signContract {
  id: string;
  tenantId: string;
  uuidDoc: string;
  nameDoc: string;
  type?: string | null;
  size?: string | null;
  pages?: string | null;
  uuidSafe?: string | null;
  safeName?: string | null;
  statusId?: string | null;
  statusName?: string | null;
  statusComment?: string | null;
  kanbanColumn?:
    | 'em_producao'
    | 'aguardando_assinatura'
    | 'assinado'
    | 'cadastrado_sistema_financeiro'
    | null;
  whoCanceled?: string | null;
  /** Último email que assinou */
  signedByEmail?: string | null;
  /** Lista de todos os emails que assinaram */
  signedByEmails?: string[] | null;
  d4signTemplateId?: string | null;
  d4signResponse?: Record<string, unknown> | null;
  targetType?: string | null;
  driveFolderId?: string | null;
  deletedAt?: string | null;
  deletedBy?: string | null;
  signedDocDriveFileId?: string | null;
  signedDocDriveUrl?: string | null;
  contaAzulRegisteredAt?: string | null;
  contaAzulRegisteredBy?: string | null;
  /** Vínculo com contrato do cliente (client_contracts) */
  clientContractId?: string | null;
  /** Vínculo com contrato do funcionário (employee_contracts) */
  employeeContractId?: string | null;
  /** Google Doc de origem (quando enviado a partir de rascunho/revisão) */
  googleDocId?: string | null;
  googleDocWebViewUrl?: string | null;
  /** Signatários persistidos localmente (atualizados no envio, webhook ou sync manual) */
  signers?: D4SignSignerItem[];
  signersSyncedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  tags?: ContractTag[];
}

export interface D4SignSignerItem {
  keySigner: string;
  email: string;
  userName?: string | null;
  userDocument?: string | null;
  signed: '0' | '1';
  assinaturaPresencialLink?: string | null;
  emailSent?: string | null;
  emailSentStatus?: string | null;
  dateSigned?: string | null;
  act?: string | null;
}

export interface ContractSignersResponse {
  contractId: string;
  uuidDoc: string;
  statusId?: string | null;
  statusName?: string | null;
  signers: D4SignSignerItem[];
}

export interface SignerItem {
  email: string;
  act: number;
}

export interface ContractDraft {
  id: string;
  tenantId: string;
  nameDocument: string;
  googleDocId: string;
  embedEditUrl?: string | null;
  wordTemplateId?: string | null;
  variables: Record<string, string>;
  driveFolderId?: string | null;
  kanbanColumn?: 'em_producao' | 'aguardando_assinatura' | 'assinado' | null;
  /** Lista ordenada de signatários (email + ação) para pré-preencher na tela de envio. act: 1=Assinar, 2=Aprovar, etc. */
  signers?: SignerItem[];
  deletedAt?: string | null;
  deletedBy?: string | null;
  /** Vínculo com contrato do cliente */
  clientContractId?: string | null;
  /** Vínculo com contrato do funcionário */
  employeeContractId?: string | null;
  createdAt: string;
  updatedAt: string;
  tags?: ContractTag[];
}

export interface ContractTag {
  id: string;
  tenantId: string;
  name: string;
  color: string;
  createdBy?: string | null;
  createdByEmail?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContractLogItem {
  id: string;
  entityType: string;
  draftId?: string | null;
  contractId?: string | null;
  action: string;
  message?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  createdBy?: string | null;
  createdByEmail?: string | null;
}

export type ContractListItem =
  | { kind: 'draft'; draft: ContractDraft }
  | { kind: 'd4sign'; contract: D4signContract };

export interface ContractListResponse {
  items: ContractListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export type ContractKanbanColumn =
  | 'em_producao'
  | 'aguardando_assinatura'
  | 'assinado'
  | 'cadastrado_sistema_financeiro';

export type DraftKanbanColumn = 'em_producao' | 'aguardando_assinatura';

export const contractManagementService = {
  // Safes
  listSafes(): Promise<D4SignSafe[]> {
    return api.get<D4SignSafe[]>('/contract-management/safes').then((r) => r.data);
  },

  // Tags
  listTags(): Promise<ContractTag[]> {
    return api.get<ContractTag[]>('/contract-management/tags').then((r) => r.data);
  },
  createTag(data: { name: string; color?: string }): Promise<ContractTag> {
    return api.post<ContractTag>('/contract-management/tags', data).then((r) => r.data);
  },
  deleteTag(id: string): Promise<{ deleted: boolean }> {
    return api.delete<{ deleted: boolean }>(`/contract-management/tags/${id}`).then((r) => r.data);
  },

  // Word templates
  listWordTemplates(): Promise<WordTemplate[]> {
    return api.get<WordTemplate[]>('/contract-management/word-templates').then((r) => r.data);
  },
  getWordTemplate(id: string): Promise<WordTemplate> {
    return api.get<WordTemplate>(`/contract-management/word-templates/${id}`).then((r) => r.data);
  },
  createWordTemplate(data: {
    name: string;
    description?: string;
    content?: string;
    googleDocsUrl?: string;
    targetType?: 'employee' | 'client' | 'both';
    placeholders?: string[];
    placeholderConfig?: Record<string, PlaceholderConfigItem>;
  }): Promise<WordTemplate> {
    return api.post<WordTemplate>('/contract-management/word-templates', data).then((r) => r.data);
  },
  updateWordTemplate(
    id: string,
    data: Partial<{
      name: string;
      description: string;
      content: string;
      googleDocsUrl: string;
      targetType: 'employee' | 'client' | 'both';
      placeholderConfig: Record<string, PlaceholderConfigItem>;
    }>,
  ): Promise<WordTemplate> {
    return api
      .put<WordTemplate>(`/contract-management/word-templates/${id}`, data)
      .then((r) => r.data);
  },
  deleteWordTemplate(id: string): Promise<{ deleted: boolean }> {
    return api
      .delete<{ deleted: boolean }>(`/contract-management/word-templates/${id}`)
      .then((r) => r.data);
  },
  extractPlaceholders(content: string): Promise<{ placeholders: string[] }> {
    return api
      .post<{
        placeholders: string[];
      }>('/contract-management/word-templates/extract-placeholders', { content })
      .then((r) => r.data);
  },

  // D4Sign templates
  listD4signTemplates(): Promise<D4signTemplate[]> {
    return api.get<D4signTemplate[]>('/contract-management/d4sign-templates').then((r) => r.data);
  },
  getD4signTemplate(id: string): Promise<D4signTemplate> {
    return api
      .get<D4signTemplate>(`/contract-management/d4sign-templates/${id}`)
      .then((r) => r.data);
  },
  createD4signTemplate(formData: FormData): Promise<D4signTemplate> {
    return api
      .post<D4signTemplate>('/contract-management/d4sign-templates', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
  deleteD4signTemplate(id: string): Promise<{ deleted: boolean }> {
    return api
      .delete<{ deleted: boolean }>(`/contract-management/d4sign-templates/${id}`)
      .then((r) => r.data);
  },

  // Contracts
  listContracts(params?: {
    page?: number;
    limit?: number;
    search?: string;
    sourceFilter?: 'all' | 'drafts' | 'd4sign';
    statusFilter?: string;
    showDeleted?: 'active' | 'deleted' | 'all';
    tagIds?: string[];
    startDate?: string;
    endDate?: string;
  }): Promise<ContractListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.search) searchParams.set('search', params.search);
    if (params?.sourceFilter) searchParams.set('source', params.sourceFilter);
    if (params?.statusFilter) searchParams.set('status', params.statusFilter);
    if (params?.showDeleted) searchParams.set('showDeleted', params.showDeleted);
    if (params?.tagIds && params.tagIds.length > 0) {
      searchParams.set('tagIds', params.tagIds.join(','));
    }
    if (params?.startDate) searchParams.set('startDate', params.startDate);
    if (params?.endDate) searchParams.set('endDate', params.endDate);
    const qs = searchParams.toString();
    const url = qs ? `/contract-management/contracts?${qs}` : '/contract-management/contracts';
    return api.get<ContractListResponse>(url).then((r) => r.data);
  },
  getContract(id: string): Promise<D4signContract> {
    return api.get<D4signContract>(`/contract-management/contracts/${id}`).then((r) => r.data);
  },
  moveContractKanban(id: string, column: ContractKanbanColumn): Promise<D4signContract> {
    return api
      .patch<D4signContract>(`/contract-management/contracts/${id}/kanban`, { column })
      .then((r) => r.data);
  },
  addTagToContract(id: string, tagId: string): Promise<{ ok: boolean }> {
    return api
      .post<{ ok: boolean }>(`/contract-management/contracts/${id}/tags/${tagId}`)
      .then((r) => r.data);
  },
  removeTagFromContract(id: string, tagId: string): Promise<{ ok: boolean }> {
    return api
      .delete<{ ok: boolean }>(`/contract-management/contracts/${id}/tags/${tagId}`)
      .then((r) => r.data);
  },
  listContractLogs(id: string): Promise<ContractLogItem[]> {
    return api
      .get<ContractLogItem[]>(`/contract-management/contracts/${id}/logs`)
      .then((r) => r.data);
  },
  markContractAsContaAzulRegistered(
    id: string,
    options?: { confirmPartialSignature?: boolean },
  ): Promise<{ ok: boolean; alreadyMarked?: boolean; contract?: D4signContract }> {
    return api
      .post<{
        ok: boolean;
        alreadyMarked?: boolean;
        contract?: D4signContract;
      }>(`/contract-management/contracts/${id}/mark-conta-azul`, options?.confirmPartialSignature ? { confirmPartialSignature: true } : undefined)
      .then((r) => r.data);
  },
  listContractSigners(id: string): Promise<ContractSignersResponse> {
    return api
      .get<ContractSignersResponse>(`/contract-management/contracts/${id}/signers`)
      .then((r) => r.data);
  },
  getContractSignatureLink(contractId: string, keySigner: string): Promise<{ url: string }> {
    return api
      .get<{
        url: string;
      }>(`/contract-management/contracts/${contractId}/signers/${encodeURIComponent(keySigner)}/signature-link`)
      .then((r) => r.data);
  },
  syncContractSigners(id: string): Promise<{
    ok: boolean;
    contract: D4signContract;
    signers: D4SignSignerItem[];
    statusId?: string | null;
    statusName?: string | null;
  }> {
    return api.post(`/contract-management/contracts/${id}/sync-signers`).then((r) => r.data);
  },
  syncContractStatus(id: string): Promise<{ ok: boolean; contract: D4signContract }> {
    return api
      .post<{
        ok: boolean;
        contract: D4signContract;
      }>(`/contract-management/contracts/${id}/sync-status`)
      .then((r) => r.data);
  },
  createContract(data: {
    d4signTemplateId: string;
    nameDocument: string;
    uuidFolder?: string;
    variables: Record<string, string>;
    targetType?: 'employee' | 'client';
    driveFolderId?: string;
  }): Promise<D4signContract> {
    return api.post<D4signContract>('/contract-management/contracts', data).then((r) => r.data);
  },

  /** Criar contrato de cliente a partir de contrato D4Sign já assinado e vincular */
  linkClientContractFromD4Sign(
    contractId: string,
    body: { clientId: string } & Omit<CreateClientContractDto, 'clientId'>,
  ) {
    return api
      .post(`/contract-management/contracts/${contractId}/link-client-contract`, body)
      .then((r) => r.data);
  },

  /** Criar contrato de funcionário a partir de contrato D4Sign já assinado e vincular */
  linkEmployeeContractFromD4Sign(
    contractId: string,
    body: { userId: string } & EmployeeCreateContractDto,
  ) {
    return api
      .post(`/contract-management/contracts/${contractId}/link-employee-contract`, body)
      .then((r) => r.data);
  },

  /** Remove contrato D4Sign (soft delete). O backend cancela no D4Sign quando houver documento enviado. */
  deleteContract(id: string): Promise<{ deleted: boolean }> {
    return api
      .delete<{ deleted: boolean }>(`/contract-management/contracts/${id}`)
      .then((r) => r.data);
  },

  downloadContractFile(id: string): Promise<void> {
    return api
      .get<ArrayBuffer>(`/contract-management/contracts/${id}/download`, {
        responseType: 'arraybuffer',
      })
      .then((response) => {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const contentDisposition = response.headers?.['content-disposition'];
        link.download =
          parseFilenameFromContentDisposition(contentDisposition) ||
          `contrato-assinado_${new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '-')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      });
  },

  /** Gerar Google Doc do modelo com placeholders preenchidos para revisão (não envia ao D4Sign). Usa Service Account no backend. */
  generateDocForReview(data: {
    wordTemplateId: string;
    nameDocument: string;
    variables: Record<string, string>;
    driveFolderId?: string;
    signers?: SignerItem[];
    clientContractId?: string;
    employeeContractId?: string;
  }): Promise<{
    fileId: string;
    embedEditUrl: string;
    webViewLink: string | null;
    name: string;
    draftId: string | null;
  }> {
    return api
      .post<{
        fileId: string;
        embedEditUrl: string;
        webViewLink: string | null;
        name: string;
        draftId: string | null;
      }>('/contract-management/generate-doc-for-review', data)
      .then((r) => r.data);
  },

  /** Enfileira envio para assinatura no D4Sign. O documento será processado em background com retentativas. */
  sendForSignature(data: {
    wordTemplateId: string;
    nameDocument: string;
    variables: Record<string, string>;
    signers: SignerItem[];
    uuidSafe: string;
    uuidFolder?: string;
    message?: string;
    googleDocId?: string;
    draftId?: string;
    clientContractId?: string;
    employeeContractId?: string;
  }): Promise<{ jobId: string; status: 'queued'; position: number }> {
    return api
      .post<{
        jobId: string;
        status: 'queued';
        position: number;
      }>('/contract-management/send-for-signature', data)
      .then((r) => r.data);
  },

  /** Consultar status do job de envio para assinatura */
  getSendJobStatus(jobId: string): Promise<{
    status: 'queued' | 'active' | 'completed' | 'failed' | 'delayed' | 'unknown';
    position?: number;
    estimatedMinutes?: number;
    estimatedAt?: string;
    contractId?: string;
    error?: string;
    attemptsMade?: number;
    attemptsLeft?: number;
  }> {
    return api
      .get<{
        status: 'queued' | 'active' | 'completed' | 'failed' | 'delayed' | 'unknown';
        position?: number;
        estimatedMinutes?: number;
        estimatedAt?: string;
        contractId?: string;
        error?: string;
        attemptsMade?: number;
        attemptsLeft?: number;
      }>(`/contract-management/send-jobs/${jobId}`)
      .then((r) => r.data);
  },

  /** Cancelar job de envio (apenas se ainda estiver na fila) */
  cancelSendJob(jobId: string): Promise<{ cancelled: boolean; message?: string }> {
    return api
      .post<{
        cancelled: boolean;
        message?: string;
      }>(`/contract-management/send-jobs/${jobId}/cancel`)
      .then((r) => r.data);
  },

  // Drafts (contratos em produção)
  listDrafts(): Promise<ContractDraft[]> {
    return api.get<ContractDraft[]>('/contract-management/drafts').then((r) => r.data);
  },
  getDraft(id: string): Promise<ContractDraft> {
    return api.get<ContractDraft>(`/contract-management/drafts/${id}`).then((r) => r.data);
  },
  moveDraftKanban(id: string, column: DraftKanbanColumn): Promise<ContractDraft> {
    return api
      .patch<ContractDraft>(`/contract-management/drafts/${id}/kanban`, { column })
      .then((r) => r.data);
  },
  updateDraft(id: string, signers: SignerItem[]): Promise<ContractDraft> {
    return api
      .patch<ContractDraft>(`/contract-management/drafts/${id}`, { signers })
      .then((r) => r.data);
  },
  addTagToDraft(id: string, tagId: string): Promise<{ ok: boolean }> {
    return api
      .post<{ ok: boolean }>(`/contract-management/drafts/${id}/tags/${tagId}`)
      .then((r) => r.data);
  },
  removeTagFromDraft(id: string, tagId: string): Promise<{ ok: boolean }> {
    return api
      .delete<{ ok: boolean }>(`/contract-management/drafts/${id}/tags/${tagId}`)
      .then((r) => r.data);
  },
  listDraftLogs(id: string): Promise<ContractLogItem[]> {
    return api.get<ContractLogItem[]>(`/contract-management/drafts/${id}/logs`).then((r) => r.data);
  },
  /** Garante que o usuário atual tenha permissão no doc (evita conflito de conta Google no embed). */
  addViewerToDraft(id: string): Promise<{ ok: boolean }> {
    return api
      .post<{ ok: boolean }>(`/contract-management/drafts/${id}/add-viewer`)
      .then((r) => r.data);
  },
  refreshDraftGoogleDocPermissions(
    id: string,
  ): Promise<{ ok: boolean; fileId: string; emails: string[] }> {
    return api
      .post<{
        ok: boolean;
        fileId: string;
        emails: string[];
      }>(`/contract-management/drafts/${id}/refresh-google-doc-permissions`)
      .then((r) => r.data);
  },
  refreshContractGoogleDocPermissions(
    id: string,
  ): Promise<{ ok: boolean; fileId: string; emails: string[] }> {
    return api
      .post<{
        ok: boolean;
        fileId: string;
        emails: string[];
      }>(`/contract-management/contracts/${id}/refresh-google-doc-permissions`)
      .then((r) => r.data);
  },
  deleteDraft(id: string): Promise<{ deleted: boolean }> {
    return api
      .delete<{ deleted: boolean }>(`/contract-management/drafts/${id}`)
      .then((r) => r.data);
  },

  // Google Drive – criar novo documento (OAuth; para compatibilidade)
  createGoogleDoc(
    accessToken: string,
    title?: string,
    driveFolderId?: string,
  ): Promise<{
    fileId: string;
    webViewLink: string | null;
    embedEditUrl: string;
    name: string;
  }> {
    return api
      .post<{
        fileId: string;
        webViewLink: string | null;
        embedEditUrl: string;
        name: string;
      }>('/google-drive/create-doc', { accessToken, title, driveFolderId })
      .then((r) => r.data);
  },

  /** Criar novo Google Doc na pasta de templates (Service Account). Usa GOOGLE_DRIVE_FOLDER_ID_TEMPLATES no backend. */
  createTemplateDoc(title?: string): Promise<{
    fileId: string;
    embedEditUrl: string;
    webViewLink: string | null;
    name: string;
  }> {
    return api
      .post<{
        fileId: string;
        embedEditUrl: string;
        webViewLink: string | null;
        name: string;
      }>('/google-drive/create-doc-in-folder', { title: title?.trim() || undefined, folderType: 'templates' })
      .then((r) => r.data);
  },

  // Google Drive – listar pastas (para escolher onde salvar template/contrato)
  getGoogleDriveFolders(
    accessToken: string,
    pageToken?: string,
  ): Promise<{
    files: { id: string; name: string; parents: string[] }[];
    nextPageToken: string | null;
  }> {
    return api
      .post<{
        files: { id: string; name: string; parents: string[] }[];
        nextPageToken: string | null;
      }>('/google-drive/folders', { accessToken, pageToken })
      .then((r) => r.data);
  },

  /** Listar pastas dentro de uma pasta pai (ou raiz se parentId omitido) */
  listFoldersByParent(
    accessToken: string,
    parentId?: string | null,
  ): Promise<{
    files: { id: string; name: string; parents: string[] }[];
    nextPageToken: string | null;
  }> {
    return api
      .post<{
        files: { id: string; name: string; parents: string[] }[];
        nextPageToken: string | null;
      }>('/google-drive/folders-by-parent', { accessToken, parentId: parentId ?? undefined })
      .then((r) => r.data);
  },

  /** Caminho da pasta (raiz > Pasta1 > Pasta2) como array de { id, name } */
  getFolderPath(
    accessToken: string,
    folderId: string,
  ): Promise<Array<{ id: string; name: string }>> {
    return api
      .post<
        Array<{ id: string; name: string }>
      >('/google-drive/folder-path', { accessToken, folderId })
      .then((r) => r.data);
  },

  /** Extrair placeholders ${NOME} de um Google Doc (leitura automática via Service Account). O doc deve estar compartilhado com a Service Account. */
  getDocPlaceholders(fileId: string): Promise<{ placeholders: string[] }> {
    return api
      .post<{ placeholders: string[] }>('/contract-management/get-doc-placeholders', { fileId })
      .then((r) => r.data);
  },

  // Google Drive – metadados do doc para embed (webViewLink + embedEditUrl com rm=minimal)
  getGoogleDocInfo(
    accessToken: string,
    fileId: string,
  ): Promise<{
    fileId: string;
    webViewLink: string | null;
    embedEditUrl: string;
    name: string | null;
    mimeType: string | null;
  }> {
    return api
      .post<{
        fileId: string;
        webViewLink: string | null;
        embedEditUrl: string;
        name: string | null;
        mimeType: string | null;
      }>('/google-drive/doc-info', { accessToken, fileId })
      .then((r) => r.data);
  },
};
